import { useRef } from "react";
import { motion } from "motion/react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

function fmtWon(n: number): string {
  if (n === 0) return "0원";
  if (n < 0) return "-" + Math.abs(n).toLocaleString() + "원";
  return n.toLocaleString() + "원";
}

function fmtShort(n: number): string {
  const abs = Math.abs(n);
  const sign = n < 0 ? "-" : "";
  if (abs >= 100000000) return sign + (abs / 100000000).toFixed(1) + "억원";
  if (abs >= 10000) return sign + Math.round(abs / 10000) + "만원";
  return sign + n.toLocaleString() + "원";
}

export default function BidCalculatorGuidePage() {
  const printRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-[#FAFAF8] font-['Noto_Sans_KR']">
      <Navbar />

      {/* Print Button - hidden during print */}
      <div className="print-hide fixed bottom-6 right-6 z-40">
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handlePrint}
          className="flex items-center gap-2 px-5 py-3 bg-[#1C1917] text-white rounded-full shadow-lg hover:bg-[#C4956A] transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-printer-line"></i>
          <span className="text-sm font-medium">PDF 저장 / 인쇄</span>
        </motion.button>
      </div>

      {/* Main Content */}
      <main className="pt-24 pb-16 px-4 md:px-6">
        <div ref={printRef} className="max-w-[960px] mx-auto">

          {/* ============ COVER ============ */}
          <header className="mb-12 text-center print:mb-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#1C1917] text-white text-xs font-medium rounded-full mb-5">
              <i className="ri-calculator-line text-[#C4956A]"></i>
              Technical Reference Document v1.0
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#1C1917] mb-3">
              경매 입찰가 계산기 — 계산법 명세서
            </h1>
            <p className="text-sm text-[#8C8780] max-w-xl mx-auto leading-relaxed">
              일반방식(Cost Deduction)과 시세역산방식(Top-Down Reverse Pricing)의
              완전한 계산 공식, 참조 테이블, 그리고 단계별 산출 예시를 포함합니다.
              본 문서만으로 다른 프로그램(Excel, Python 등)에 동일한 계산 로직을 구현할 수 있습니다.
            </p>
            <div className="mt-6 flex items-center justify-center gap-4 text-xs text-[#B8B3AE]">
              <span>작성일: 2026-08-03</span>
              <span className="text-[#E0DCD6]">|</span>
              <span>대상: 강스터디 입찰계산기 v2.0</span>
              <span className="text-[#E0DCD6]">|</span>
              <span>페이지: 1/8</span>
            </div>
          </header>

          {/* ============ TABLE OF CONTENTS ============ */}
          <section className="mb-12 print:mb-8 print-break-after">
            <h2 className="text-lg font-bold text-[#1C1917] mb-4 pb-2 border-b-2 border-[#1C1917]">
              목차
            </h2>
            <div className="space-y-3 text-sm">
              {[
                { num: "1", label: "핵심 참조 테이블 (3종 하드코딩 대상)", sub: "연식가중치 · 명도비 · 취득세율" },
                { num: "2", label: "일반방식 (Cost Deduction)", sub: "공식 · 산출 흐름 · 예시" },
                { num: "3", label: "시세역산방식 (Top-Down Reverse Pricing)", sub: "공식 · 산출 흐름 · 민감도 분석" },
                { num: "4", label: "두 방식 비교표", sub: "" },
                { num: "5", label: "개발자 구현 가이드", sub: "의사코드 · 주의사항" },
              ].map((item, i) => (
                <a
                  key={i}
                  href={`#section-${item.num}`}
                  className="flex items-center gap-3 text-[#5C5650] hover:text-[#C4956A] transition-colors group"
                >
                  <span className="w-7 h-7 rounded-md bg-[#1C1917] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">
                    {item.num}
                  </span>
                  <span className="font-medium">{item.label}</span>
                  {item.sub && (
                    <span className="text-xs text-[#B8B3AE] ml-auto hidden sm:inline">{item.sub}</span>
                  )}
                </a>
              ))}
            </div>
          </section>

          {/* ============ SECTION 1: LOOKUP TABLES ============ */}
          <section id="section-1" className="mb-12 print:mb-8">
            <h2 className="text-lg font-bold text-[#1C1917] mb-2 pb-2 border-b-2 border-[#1C1917]">
              1. 핵심 참조 테이블 — 하드코딩 대상 3종
            </h2>
            <p className="text-xs text-[#8C8780] mb-6">
              아래 세 가지 테이블은 계산의 근간이며, 외부 API나 DB 조회 없이 <strong className="text-[#1C1917]">코드 내 상수·조건문으로 직접 정의</strong>해야 합니다.
              값이 변경될 일이 거의 없으므로, 별도 설정 파일 없이 하드코딩하는 것이 가장 단순하고 신뢰성 높은 접근입니다.
            </p>

            {/* Table 1: Age Weight */}
            <div className="bg-white rounded-xl border border-[#E8E5E0] p-6 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 rounded bg-amber-100 flex items-center justify-center">
                  <span className="text-[10px] font-bold text-amber-700">A</span>
                </div>
                <h3 className="text-sm font-bold text-[#1C1917]">연식 가중치표 (Age Weight Table)</h3>
              </div>
              <p className="text-xs text-[#5C5650] mb-4">
                수리비(C<sub>rep</sub>) = 전용면적(m²) × 50,000원 × <strong>연식가중치</strong>
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E8E5E0]">
                      <th className="text-left py-2.5 text-xs text-[#8C8780] font-medium w-1/2">건물 연식 (년)</th>
                      <th className="text-right py-2.5 text-xs text-[#8C8780] font-medium w-1/4">가중치</th>
                      <th className="text-left py-2.5 text-xs text-[#8C8780] font-medium w-1/4">의미</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { range: "0 ~ 5년", weight: "0.5", meaning: "신축급 — 수리비 50%" },
                      { range: "6 ~ 10년", weight: "1.0", meaning: "표준 — 수리비 100%" },
                      { range: "11 ~ 20년", weight: "1.5", meaning: "노후화 진행 — 수리비 150%" },
                      { range: "21년 이상", weight: "2.0", meaning: "고령 — 수리비 200%" },
                    ].map((row, i) => (
                      <tr key={i} className="border-b border-[#F0EDE8] last:border-0">
                        <td className="py-2.5 text-[#1C1917] font-medium">{row.range}</td>
                        <td className="py-2.5 text-right font-mono font-bold text-[#C4956A]">{row.weight}</td>
                        <td className="py-2.5 text-xs text-[#5C5650]">{row.meaning}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 bg-[#FAFAF8] rounded-lg p-3 text-xs font-mono text-[#5C5650]">
                <span className="text-[#8C8780]">// 의사코드</span><br />
                <strong>function</strong> getAgeWeight(age) {"{"}<br />
                {"  "}<strong>if</strong> (age {"<="} 5) <strong>return</strong> 0.5;<br />
                {"  "}<strong>if</strong> (age {"<="} 10) <strong>return</strong> 1.0;<br />
                {"  "}<strong>if</strong> (age {"<="} 20) <strong>return</strong> 1.5;<br />
                {"  "}<strong>return</strong> 2.0;<br />
                {"}"}
              </div>
            </div>

            {/* Table 2: Eviction Cost */}
            <div className="bg-white rounded-xl border border-[#E8E5E0] p-6 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 rounded bg-rose-100 flex items-center justify-center">
                  <span className="text-[10px] font-bold text-rose-700">B</span>
                </div>
                <h3 className="text-sm font-bold text-[#1C1917]">명도비 추정표 (Eviction Cost Table)</h3>
              </div>
              <p className="text-xs text-[#5C5650] mb-4">
                면적(m²) 기준 최소금액과의 비교로 산출하며, 점유자 유형별로 다른 최소금액과 단가를 적용합니다.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E8E5E0]">
                      <th className="text-left py-2.5 text-xs text-[#8C8780] font-medium">점유자 유형</th>
                      <th className="text-right py-2.5 text-xs text-[#8C8780] font-medium">계산식</th>
                      <th className="text-right py-2.5 text-xs text-[#8C8780] font-medium">최소금액</th>
                      <th className="text-right py-2.5 text-xs text-[#8C8780] font-medium">㎡당 단가</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { type: "점유자 없음", formula: "0원", min: "0원", unit: "0원" },
                      { type: "채무자 점유", formula: "max(200만원, 면적×3만원)", min: "2,000,000원", unit: "30,000원" },
                      { type: "임차인 (대항력 없음)", formula: "max(300만원, 면적×5만원)", min: "3,000,000원", unit: "50,000원" },
                      { type: "임차인 (대항력 있음)", formula: "max(1,000만원, 면적×12만원)", min: "10,000,000원", unit: "120,000원" },
                      { type: "유치권자", formula: "max(1,500만원, 면적×15만원)", min: "15,000,000원", unit: "150,000원" },
                    ].map((row, i) => (
                      <tr key={i} className="border-b border-[#F0EDE8] last:border-0">
                        <td className="py-2.5 text-[#1C1917] font-medium text-xs whitespace-nowrap">{row.type}</td>
                        <td className="py-2.5 text-right font-mono text-xs text-[#C4956A]">{row.formula}</td>
                        <td className="py-2.5 text-right text-xs text-[#5C5650]">{row.min}</td>
                        <td className="py-2.5 text-right text-xs text-[#5C5650]">{row.unit}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 bg-[#FAFAF8] rounded-lg p-3 text-xs font-mono text-[#5C5650]">
                <span className="text-[#8C8780]">// 의사코드</span><br />
                <strong>function</strong> getEvictionCost(area, occupantType) {"{"}<br />
                {"  "}<strong>switch</strong> (occupantType) {"{"}<br />
                {"    "}<strong>case</strong> "debtor": <strong>return</strong> max(2000000, area * 30000);<br />
                {"    "}<strong>case</strong> "tenant_no_rights": <strong>return</strong> max(3000000, area * 50000);<br />
                {"    "}<strong>case</strong> "tenant_with_rights": <strong>return</strong> max(10000000, area * 120000);<br />
                {"    "}<strong>case</strong> "lien": <strong>return</strong> max(15000000, area * 150000);<br />
                {"    "}<strong>default</strong>: <strong>return</strong> 0;<br />
                {"  "}{"}"}<br />
                {"}"}
              </div>
            </div>

            {/* Table 3: Acquisition Tax Rate */}
            <div className="bg-white rounded-xl border border-[#E8E5E0] p-6 mb-6">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 rounded bg-emerald-100 flex items-center justify-center">
                  <span className="text-[10px] font-bold text-emerald-700">C</span>
                </div>
                <h3 className="text-sm font-bold text-[#1C1917]">취득세율표 (Acquisition Tax Rate Table)</h3>
              </div>
              <p className="text-xs text-[#5C5650] mb-4">
                취득세(C<sub>tax</sub>) = 기준가격 × <strong>취득세율</strong>. 일반방식은 추정매각시세(V<sub>est</sub>) 기준,
                시세역산방식은 급매시세(V) 기준입니다.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E8E5E0]">
                      <th className="text-left py-2.5 text-xs text-[#8C8780] font-medium">보유 주택 수</th>
                      <th className="text-left py-2.5 text-xs text-[#8C8780] font-medium">매매가 구간</th>
                      <th className="text-right py-2.5 text-xs text-[#8C8780] font-medium">세율</th>
                      <th className="text-right py-2.5 text-xs text-[#8C8780] font-medium">소수점</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { homes: "무주택 / 1주택", range: "6억원 이하", rate: "1.1%", decimal: "0.011" },
                      { homes: "무주택 / 1주택", range: "6억 초과 ~ 9억 이하", rate: "1.3%", decimal: "0.013" },
                      { homes: "무주택 / 1주택", range: "9억원 초과", rate: "3.5%", decimal: "0.035" },
                      { homes: "2주택자", range: "구간 무관", rate: "8.0%", decimal: "0.08" },
                      { homes: "3주택 이상", range: "구간 무관", rate: "12.0%", decimal: "0.12" },
                    ].map((row, i) => (
                      <tr key={i} className="border-b border-[#F0EDE8] last:border-0">
                        <td className="py-2.5 text-[#1C1917] font-medium text-xs">{row.homes}</td>
                        <td className="py-2.5 text-xs text-[#5C5650]">{row.range}</td>
                        <td className="py-2.5 text-right font-mono font-bold text-[#C4956A]">{row.rate}</td>
                        <td className="py-2.5 text-right font-mono text-xs text-[#8C8780]">{row.decimal}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 bg-[#FAFAF8] rounded-lg p-3 text-xs font-mono text-[#5C5650]">
                <span className="text-[#8C8780]">// 의사코드</span><br />
                <strong>function</strong> getTaxRate(homeCount, price) {"{"}<br />
                {"  "}<strong>if</strong> (homeCount {"<="} 1) {"{"}<br />
                {"    "}<strong>if</strong> (price {"<="} 600_000_000) <strong>return</strong> 0.011;<br />
                {"    "}<strong>if</strong> (price {"<="} 900_000_000) <strong>return</strong> 0.013;<br />
                {"    "}<strong>return</strong> 0.035;<br />
                {"  "}{"}"}<br />
                {"  "}<strong>if</strong> (homeCount {"==="} 2) <strong>return</strong> 0.08;<br />
                {"  "}<strong>return</strong> 0.12;<br />
                {"}"}
              </div>
            </div>
          </section>

          {/* ============ SECTION 2: NORMAL METHOD ============ */}
          <section id="section-2" className="mb-12 print:mb-8 print-break-before">
            <h2 className="text-lg font-bold text-[#1C1917] mb-2 pb-2 border-b-2 border-[#1C1917]">
              2. 일반방식 (Cost Deduction Method)
            </h2>
            <p className="text-xs text-[#8C8780] mb-6">
              매각 시세에서 모든 부대비용·권리금·목표수익을 공제하여 입찰 상한가를 산출합니다.
              초보 투자자에게 적합하며, 지역 낙찰가율을 반영한 추천가를 함께 제공합니다.
            </p>

            {/* Core Formula */}
            <div className="bg-gradient-to-r from-[#1C1917] to-[#3A2E28] rounded-xl p-6 mb-6">
              <p className="text-[10px] uppercase tracking-wide text-[#8C8780] mb-2">핵심 공식</p>
              <p className="text-xl md:text-2xl font-bold text-white font-mono mb-3">
                P<sub className="text-[#C4956A]">max</sub> = V<sub className="text-[#C4956A]">est</sub> - (R + C<sub>rep</sub> + C<sub>tax</sub> + C<sub>fin</sub> + C<sub>mgt</sub> + M<sub>target</sub>)
              </p>
              <div className="space-y-1 text-xs text-[#B8B3AE]">
                <p><span className="text-white font-medium">P<sub>max</sub></span> = 입찰 상한가 (Maximum Bid Price)</p>
                <p><span className="text-white font-medium">V<sub>est</sub></span> = 추정 매각 시세 (인근 급매가·실거래가 기준)</p>
              </div>
            </div>

            {/* Variable Definitions */}
            <div className="bg-white rounded-xl border border-[#E8E5E0] p-6 mb-6">
              <h3 className="text-sm font-bold text-[#1C1917] mb-4 flex items-center gap-2">
                <i className="ri-function-line text-[#C4956A]"></i>
                변수 정의
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E8E5E0]">
                      <th className="text-left py-2 text-xs text-[#8C8780] font-medium w-[14%]">기호</th>
                      <th className="text-left py-2 text-xs text-[#8C8780] font-medium w-[22%]">명칭</th>
                      <th className="text-left py-2 text-xs text-[#8C8780] font-medium w-[44%]">산출 방법</th>
                      <th className="text-left py-2 text-xs text-[#8C8780] font-medium w-[20%]">입력 방식</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { sym: "V_est", name: "추정 매각 시세", calc: "인근 동일 평형 급매 최저 실거래가 기준", input: "사용자 직접 입력" },
                      { sym: "R", name: "인수 권리금액", calc: "대항력 임차인 보증금·유치권 등 낙찰 후 인수해야 하는 금액", input: "사용자 직접 입력" },
                      { sym: "C_rep", name: "수리비", calc: "전용면적 × 50,000 × 연식가중치 (표 A 참조)", input: "자동 계산" },
                      { sym: "C_tax", name: "취득세", calc: "V_est × 취득세율 (표 C 참조)", input: "자동 계산" },
                      { sym: "C_fin", name: "금융비용", calc: "대출금액 × (대출금리/100) × (소요기간/12)", input: "반자동 (사용자 입력 + 계산)" },
                      { sym: "C_mgt", name: "미납 관리비", calc: "직접 입력값 또는 전용면적 × 20,000원 (자동추정)", input: "선택적 입력" },
                      { sym: "M_target", name: "목표 순수익", calc: "이 거래에서 얻고자 하는 최소 순수익 금액", input: "사용자 직접 입력" },
                    ].map((row, i) => (
                      <tr key={i} className="border-b border-[#F0EDE8] last:border-0">
                        <td className="py-2.5 font-mono text-xs font-bold text-[#C4956A]">{row.sym}</td>
                        <td className="py-2.5 text-xs font-medium text-[#1C1917]">{row.name}</td>
                        <td className="py-2.5 text-xs text-[#5C5650]">{row.calc}</td>
                        <td className="py-2.5 text-xs text-[#8C8780]">{row.input}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 3-Tier Output */}
            <div className="bg-white rounded-xl border border-[#E8E5E0] p-6 mb-6">
              <h3 className="text-sm font-bold text-[#1C1917] mb-4 flex items-center gap-2">
                <i className="ri-stack-line text-[#C4956A]"></i>
                출력: 3단계 입찰가
              </h3>
              <div className="space-y-3">
                {[
                  {
                    label: "보수적 입찰가 (Safe Bid)",
                    formula: "P_max × 0.95",
                    desc: "안전마진 극대화. 낙찰 확률은 낮으나 수익률을 최대한 높입니다.",
                    icon: "ri-shield-check-line",
                    color: "emerald",
                  },
                  {
                    label: "적정 추천가 (Recommended Bid)",
                    formula: "min(P_max, V_est × 지역낙찰가율/100)",
                    desc: "낙찰 가능성과 수익성의 최적 밸런스. 강스터디 추천 입찰가입니다.",
                    icon: "ri-star-line",
                    color: "amber",
                  },
                  {
                    label: "공격적 입찰가 (Aggressive Bid)",
                    formula: "P_max",
                    desc: "수익성 한계선까지 투찰. 낙찰 확률 최대화, 수익은 최소화.",
                    icon: "ri-rocket-line",
                    color: "rose",
                  },
                ].map((tier, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 bg-[#FAFAF8] rounded-lg">
                    <div className={`w-8 h-8 rounded-lg bg-${tier.color}-100 flex items-center justify-center flex-shrink-0`}>
                      <i className={`${tier.icon} text-${tier.color}-600 text-sm`}></i>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-bold text-[#1C1917]">{tier.label}</span>
                        <span className="px-1.5 py-0.5 bg-[#E8E5E0] text-[10px] font-mono text-[#5C5650] rounded">{tier.formula}</span>
                      </div>
                      <p className="text-xs text-[#8C8780]">{tier.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Step-by-step Example */}
            <div className="bg-white rounded-xl border border-[#E8E5E0] p-6 mb-6">
              <h3 className="text-sm font-bold text-[#1C1917] mb-4 flex items-center gap-2">
                <i className="ri-numbers-line text-[#C4956A]"></i>
                계산 예시 (실제 입력값 기준)
              </h3>
              <div className="bg-[#FAFAF8] rounded-lg p-4 mb-4">
                <p className="text-xs font-medium text-[#1C1917] mb-2">입력값</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-[#5C5650]">
                  <p>전용면적: 85m²</p>
                  <p>건물 연식: 15년 → 가중치 1.5</p>
                  <p>추정 매각 시세(V<sub>est</sub>): 380,000,000원</p>
                  <p>지역 낙찰가율: 85%</p>
                  <p>인수 권리금(R): 0원</p>
                  <p>점유자: 없음 → 명도비 0원</p>
                  <p>보유 주택: 1주택 → 세율 1.1%</p>
                  <p>대출금액: 150,000,000원, 금리 4.5%, 6개월</p>
                  <p>미납 관리비: 1,500,000원</p>
                  <p>목표 순수익(M<sub>target</sub>): 50,000,000원</p>
                </div>
              </div>
              <div className="space-y-2 text-xs">
                {[
                  { label: "① 수리비 (C_rep)", calc: "85 × 50,000 × 1.5", result: 6375000 },
                  { label: "② 명도비", calc: "점유자 없음 → 0", result: 0 },
                  { label: "③ 취득세 (C_tax)", calc: "380,000,000 × 0.011", result: 4180000 },
                  { label: "④ 금융비용 (C_fin)", calc: "150,000,000 × 0.045 × (6/12)", result: 3375000 },
                  { label: "⑤ 미납 관리비 (C_mgt)", calc: "직접 입력값", result: 1500000 },
                  { label: "⑥ 총 부대비용 (합계)", calc: "①+②+③+④+⑤", result: 15430000 },
                  { label: "⑦ P_max", calc: "380,000,000 - 0 - 15,430,000 - 50,000,000", result: 314570000 },
                  { label: "⑧ 보수적 입찰가", calc: "P_max × 0.95", result: 298841500 },
                  { label: "⑨ 지역낙찰가율 반영가", calc: "380,000,000 × 0.85", result: 323000000 },
                  { label: "⑩ 적정 추천가", calc: "min(P_max, ⑨)", result: 314570000 },
                  { label: "⑪ 공격적 입찰가", calc: "P_max", result: 314570000 },
                ].map((step, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#F0EDE8] last:border-0">
                    <span className="text-[#5C5650]">{step.label}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] text-[#B8B3AE] hidden sm:inline">{step.calc}</span>
                      <span className="text-xs font-mono font-semibold text-[#1C1917]">{fmtWon(step.result)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ============ SECTION 3: REVERSE METHOD ============ */}
          <section id="section-3" className="mb-12 print:mb-8 print-break-before">
            <h2 className="text-lg font-bold text-[#1C1917] mb-2 pb-2 border-b-2 border-[#1C1917]">
              3. 시세역산방식 (Top-Down Reverse Pricing)
            </h2>
            <p className="text-xs text-[#8C8780] mb-6">
              법원 감정가를 배제하고 <strong className="text-[#1C1917]">실제 팔 수 있는 급매 시세</strong>에서 출발합니다.
              감정가 착시를 원천 차단하며, 투자자 관점에서 "얼마에 사야 이윤이 남는가"를 역산합니다.
              숙련된 투자자와 감정가 거품이 의심되는 물건에 특히 유용합니다.
            </p>

            {/* Core Formula */}
            <div className="bg-gradient-to-r from-[#C4956A] to-[#A07850] rounded-xl p-6 mb-6">
              <p className="text-[10px] uppercase tracking-wide text-white/60 mb-2">핵심 공식</p>
              <p className="text-xl md:text-2xl font-bold text-white font-mono mb-3">
                P = V - M - C - R
              </p>
              <div className="space-y-1 text-xs text-white/70">
                <p><span className="text-white font-medium">P</span> = 투찰 상한가</p>
                <p><span className="text-white font-medium">V</span> = 급매 시세 (국토부·KB 기준 인근 최저가)</p>
                <p><span className="text-white font-medium">M</span> = 목표 마진 (절대금액 또는 ROI%로 입력)</p>
                <p><span className="text-white font-medium">C</span> = 제반비용 합계 (7개 항목)</p>
                <p><span className="text-white font-medium">R</span> = 인수 권리금액</p>
              </div>
            </div>

            {/* Variable Definitions */}
            <div className="bg-white rounded-xl border border-[#E8E5E0] p-6 mb-6">
              <h3 className="text-sm font-bold text-[#1C1917] mb-4 flex items-center gap-2">
                <i className="ri-function-line text-[#C4956A]"></i>
                변수 정의 — C(제반비용) 상세
              </h3>
              <p className="text-xs text-[#5C5650] mb-3">
                C = C<sub>rep</sub> + 명도비 + C<sub>tax</sub> + C<sub>fin</sub> + C<sub>mgt</sub> + 중개수수료 + 기타비용
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E8E5E0]">
                      <th className="text-left py-2 text-xs text-[#8C8780] font-medium w-[18%]">비용 항목</th>
                      <th className="text-left py-2 text-xs text-[#8C8780] font-medium w-[42%]">산출 방법</th>
                      <th className="text-left py-2 text-xs text-[#8C8780] font-medium w-[20%]">일반방식과 비교</th>
                      <th className="text-left py-2 text-xs text-[#8C8780] font-medium w-[20%]">입력</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { item: "C_rep (수리비)", calc: "면적 × 50,000 × 연식가중치", comp: "동일", input: "자동" },
                      { item: "명도비", calc: "점유자 유형별 (표 B)", comp: "동일", input: "자동" },
                      { item: "C_tax (취득세)", calc: "V(급매시세) × 세율", comp: "기준가가 V로 변경", input: "자동" },
                      { item: "C_fin (금융비용)", calc: "대출금액 × 금리 × 기간", comp: "동일", input: "반자동" },
                      { item: "C_mgt (관리비)", calc: "입력값 or 면적×2만원", comp: "동일", input: "선택" },
                      { item: "중개수수료", calc: "사용자 직접 입력", comp: "일반방식엔 없음 (신규)", input: "사용자" },
                      { item: "기타 비용", calc: "사용자 직접 입력 (등기비 등)", comp: "일반방식엔 없음 (신규)", input: "사용자" },
                    ].map((row, i) => (
                      <tr key={i} className="border-b border-[#F0EDE8] last:border-0">
                        <td className="py-2.5 text-xs font-medium text-[#1C1917]">{row.item}</td>
                        <td className="py-2.5 text-xs text-[#5C5650]">{row.calc}</td>
                        <td className="py-2.5 text-xs text-[#8C8780]">{row.comp}</td>
                        <td className="py-2.5 text-xs text-[#B8B3AE]">{row.input}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* 2-Tier Output + Danger Logic */}
            <div className="bg-white rounded-xl border border-[#E8E5E0] p-6 mb-6">
              <h3 className="text-sm font-bold text-[#1C1917] mb-4 flex items-center gap-2">
                <i className="ri-stack-line text-[#C4956A]"></i>
                출력: 2단계 입찰가 + 위험 판정
              </h3>
              <div className="space-y-3 mb-5">
                {[
                  {
                    label: "보수적 입찰가 (Safe)",
                    formula: "P × 0.95",
                    desc: "안전마진 반영. 실제 투찰 시 이 가격 이하로 입찰 권장.",
                  },
                  {
                    label: "공격적 입찰가 (Aggressive) = 상한선",
                    formula: "P",
                    desc: "수익성 한계선. 이 가격을 초과하면 손실이 발생합니다.",
                  },
                ].map((tier, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 bg-[#FAFAF8] rounded-lg">
                    <div className={`w-8 h-8 rounded-lg ${i === 0 ? "bg-emerald-100" : "bg-rose-100"} flex items-center justify-center flex-shrink-0`}>
                      <i className={`${i === 0 ? "ri-shield-check-line text-emerald-600" : "ri-rocket-line text-rose-600"} text-sm`}></i>
                    </div>
                    <div>
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-xs font-bold text-[#1C1917]">{tier.label}</span>
                        <span className="px-1.5 py-0.5 bg-[#E8E5E0] text-[10px] font-mono text-[#5C5650] rounded">{tier.formula}</span>
                      </div>
                      <p className="text-xs text-[#8C8780]">{tier.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Danger Matrix */}
              <h4 className="text-xs font-bold text-[#8C8780] uppercase tracking-wide mb-3">위험 판정 로직</h4>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E8E5E0]">
                      <th className="text-left py-2 text-xs text-[#8C8780] font-medium">조건</th>
                      <th className="text-left py-2 text-xs text-[#8C8780] font-medium">판정</th>
                      <th className="text-left py-2 text-xs text-[#8C8780] font-medium">의미</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { cond: "P &lt; 0", judgment: "🔴 투찰 불가 (마지노선 초과)", meaning: "목표수익+비용이 시세를 초과. 어떤 가격으로도 손실." },
                      { cond: "0 ≤ P &lt; 최저입찰가", judgment: "🟠 입찰 불가능", meaning: "상한가가 법원 최저가보다 낮아 참여 자체가 불가능." },
                      { cond: "P ≥ 최저입찰가", judgment: "🟢 입찰 가능", meaning: "정상 입찰 가능. 목표 수익 확보 가능." },
                    ].map((row, i) => (
                      <tr key={i} className="border-b border-[#F0EDE8] last:border-0">
                        <td className="py-2.5 font-mono text-xs text-[#C4956A]">{row.cond}</td>
                        <td className="py-2.5 text-xs font-semibold text-[#1C1917]">{row.judgment}</td>
                        <td className="py-2.5 text-xs text-[#5C5650]">{row.meaning}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Sensitivity Analysis */}
            <div className="bg-white rounded-xl border border-[#E8E5E0] p-6 mb-6">
              <h3 className="text-sm font-bold text-[#1C1917] mb-4 flex items-center gap-2">
                <i className="ri-grid-line text-[#C4956A]"></i>
                민감도 분석 (Sensitivity Matrix)
              </h3>
              <p className="text-xs text-[#5C5650] mb-4">
                급매 시세(V)를 기준으로 ±10% 구간(5% 간격)의 5개 시나리오에서
                P(투찰 상한가)와 ROI를 재계산합니다. 각 시나리오별 취득세도 함께 재계산됩니다.
                <strong className="text-[#1C1917]">시세가 예상보다 낮아질 경우의 리스크를 사전에 파악</strong>할 수 있습니다.
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[#E8E5E0]">
                      <th className="text-left py-2 text-xs text-[#8C8780] font-medium">시세 변동</th>
                      <th className="text-right py-2 text-xs text-[#8C8780] font-medium">조정 시세</th>
                      <th className="text-right py-2 text-xs text-[#8C8780] font-medium">투찰 상한가(P)</th>
                      <th className="text-right py-2 text-xs text-[#8C8780] font-medium">ROI</th>
                      <th className="text-center py-2 text-xs text-[#8C8780] font-medium">판정</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { pct: -10, adjV: 342000000, p: "계산됨", roi: "재계산", ok: false },
                      { pct: -5, adjV: 361000000, p: "계산됨", roi: "재계산", ok: true },
                      { pct: 0, adjV: 380000000, p: "계산됨 (기준)", roi: "기준 ROI", ok: true, current: true },
                      { pct: +5, adjV: 399000000, p: "계산됨", roi: "재계산", ok: true },
                      { pct: +10, adjV: 418000000, p: "계산됨", roi: "재계산", ok: true },
                    ].map((row, i) => (
                      <tr key={i} className={`border-b border-[#F0EDE8] last:border-0 ${row.current ? "bg-amber-50/50" : ""}`}>
                        <td className={`py-2.5 font-semibold text-xs ${row.pct > 0 ? "text-emerald-600" : row.pct < 0 ? "text-rose-500" : "text-[#1C1917]"}`}>
                          {row.pct > 0 ? "+" : ""}{row.pct}%
                          {row.current && <span className="text-[10px] text-[#8C8780] ml-1">(현재)</span>}
                        </td>
                        <td className="py-2.5 text-right text-xs text-[#5C5650]">{fmtWon(row.adjV)}</td>
                        <td className="py-2.5 text-right text-xs text-[#5C5650]">{row.p}</td>
                        <td className="py-2.5 text-right text-xs text-[#5C5650]">{row.roi}</td>
                        <td className="py-2.5 text-center">
                          {row.ok ? (
                            <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-700 text-[10px] rounded font-medium">수익</span>
                          ) : (
                            <span className="px-1.5 py-0.5 bg-rose-100 text-rose-600 text-[10px] rounded font-medium">손실</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-[#B8B3AE] mt-2">
                * 각 시나리오에서 취득세는 조정된 시세(adjV) 기준으로 재계산됩니다.
              </p>
            </div>

            {/* Reverse Example */}
            <div className="bg-white rounded-xl border border-[#E8E5E0] p-6">
              <h3 className="text-sm font-bold text-[#1C1917] mb-4 flex items-center gap-2">
                <i className="ri-numbers-line text-[#C4956A]"></i>
                계산 예시 (동일 입력값 기준)
              </h3>
              <div className="bg-[#FAFAF8] rounded-lg p-4 mb-4">
                <p className="text-xs font-medium text-[#1C1917] mb-2">입력값</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-xs text-[#5C5650]">
                  <p>급매 시세(V): 380,000,000원</p>
                  <p>법원 감정가: 420,000,000원 (참고만)</p>
                  <p>최저 입찰가: 294,000,000원</p>
                  <p>목표 마진(M): 50,000,000원</p>
                  <p>인수 권리금(R): 0원</p>
                  <p>중개수수료: 1,900,000원</p>
                  <p>기타 비용: 0원</p>
                  <p className="text-[10px] text-[#B8B3AE] col-span-2">* 나머지(면적·연식·점유·세율·대출·관리비)는 일반방식과 동일</p>
                </div>
              </div>
              <div className="space-y-2 text-xs">
                {[
                  { label: "① 수리비 (C_rep)", calc: "85 × 50,000 × 1.5", result: 6375000 },
                  { label: "② 명도비", calc: "점유자 없음 → 0", result: 0 },
                  { label: "③ 취득세 (C_tax)", calc: "380,000,000 × 0.011 (V 기준)", result: 4180000 },
                  { label: "④ 금융비용", calc: "150,000,000 × 0.045 × 0.5", result: 3375000 },
                  { label: "⑤ 관리비", calc: "직접 입력", result: 1500000 },
                  { label: "⑥ 중개수수료", calc: "직접 입력", result: 1900000 },
                  { label: "⑦ 기타 비용", calc: "직접 입력", result: 0 },
                  { label: "⑧ 총 제반비용 (C)", calc: "①~⑦ 합계", result: 17330000 },
                  { label: "⑨ P (투찰 상한가)", calc: "380,000,000 - 50,000,000 - 17,330,000 - 0", result: 312670000 },
                  { label: "⑩ 보수적 입찰가", calc: "P × 0.95", result: 297036500 },
                  { label: "⑪ 위험 판정", calc: "P(312,670,000) ≥ 최저(294,000,000)", resultStr: "🟢 입찰 가능" },
                ].map((step, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5 border-b border-[#F0EDE8] last:border-0">
                    <span className="text-[#5C5650]">{step.label}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] text-[#B8B3AE] hidden sm:inline">{step.calc}</span>
                      <span className="text-xs font-mono font-semibold text-[#1C1917]">
                        {step.resultStr || fmtWon(step.result!)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* ============ SECTION 4: COMPARISON ============ */}
          <section id="section-4" className="mb-12 print:mb-8 print-break-before">
            <h2 className="text-lg font-bold text-[#1C1917] mb-2 pb-2 border-b-2 border-[#1C1917]">
              4. 두 방식 비교표
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-[#1C1917]">
                    <th className="text-left py-3 text-xs text-[#8C8780] font-medium w-[22%]">구분</th>
                    <th className="text-left py-3 text-xs text-[#8C8780] font-medium w-[39%]">일반방식</th>
                    <th className="text-left py-3 text-xs text-[#8C8780] font-medium w-[39%]">시세역산방식</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: "출발점", normal: "추정 매각 시세 (V_est)", reverse: "급매 시세 (V)" },
                    { label: "감정가 영향", normal: "지역 낙찰가율로 간접 반영", reverse: "완전 배제 (참고만)" },
                    { label: "목표수익 처리", normal: "비용처럼 공제 (M_target)", reverse: "명시적 마진(M)으로 차감" },
                    { label: "입찰가 단계", normal: "3단계 (Safe / Recmd / Aggr)", reverse: "2단계 (Safe / Aggr)" },
                    { label: "추천가 산출", normal: "min(P_max, V×낙찰가율)", reverse: "없음 (P 자체가 상한)" },
                    { label: "위험 경고", normal: "음수 시 경고", reverse: "3단계 판정 + 마지노선 경고" },
                    { label: "민감도 분석", normal: "없음", reverse: "±10% 5단계 시나리오 매트릭스" },
                    { label: "실시간 슬라이더", normal: "없음", reverse: "목표수익 슬라이더로 P 역산" },
                    { label: "비용 항목 수", normal: "5개 (R+C_rep+C_tax+C_fin+C_mgt)", reverse: "7개 (C에 중개수수료·기타비용 추가)" },
                    { label: "적합 상황", normal: "초보자 · 표준 물건 · 첫 입찰", reverse: "숙련자 · 급매 물건 · 감정가 거품 의심" },
                  ].map((row, i) => (
                    <tr key={i} className="border-b border-[#F0EDE8] last:border-0">
                      <td className="py-2.5 text-xs font-medium text-[#1C1917]">{row.label}</td>
                      <td className="py-2.5 text-xs text-[#5C5650]">{row.normal}</td>
                      <td className="py-2.5 text-xs text-[#5C5650]">{row.reverse}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* ============ SECTION 5: DEVELOPER GUIDE ============ */}
          <section id="section-5" className="mb-12 print:mb-8 print-break-before">
            <h2 className="text-lg font-bold text-[#1C1917] mb-2 pb-2 border-b-2 border-[#1C1917]">
              5. 개발자 구현 가이드
            </h2>
            <p className="text-xs text-[#8C8780] mb-6">
              본 명세서만으로 어떤 언어·플랫폼에서든 동일한 계산 로직을 구현할 수 있습니다.
              아래는 권장 구현 순서와 핵심 주의사항입니다.
            </p>

            <div className="bg-white rounded-xl border border-[#E8E5E0] p-6 mb-6">
              <h3 className="text-sm font-bold text-[#1C1917] mb-3">구현 순서</h3>
              <div className="space-y-3">
                {[
                  { num: 1, text: "3종 참조 테이블(연식가중치·명도비·취득세율)을 함수로 구현" },
                  { num: 2, text: "일반방식: P_max 산출 → 3단계 입찰가 계산" },
                  { num: 3, text: "시세역산방식: P 산출 → 위험 판정 → 민감도 분석" },
                  { num: 4, text: "입력값 검증: 모든 금액은 0 이상 정수, 면적은 양수, 금리는 0~100%" },
                  { num: 5, text: "결과값은 Math.round() 또는 정수 나눗셈으로 정수 처리" },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded bg-[#1C1917] flex items-center justify-center flex-shrink-0">
                      <span className="text-[10px] font-bold text-white">{item.num}</span>
                    </div>
                    <p className="text-xs text-[#5C5650] pt-0.5">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-[#E8E5E0] p-6 mb-6">
              <h3 className="text-sm font-bold text-[#1C1917] mb-3">핵심 주의사항</h3>
              <div className="space-y-3">
                {[
                  { title: "취득세 기준가격", body: "일반방식은 V_est(추정매각시세), 시세역산방식은 V(급매시세)를 기준으로 합니다. 두 방식의 기준가격이 다르므로 취득세액도 달라집니다." },
                  { title: "음수 처리", body: "P_max 또는 P가 음수이면 '투찰 불가' 상태입니다. 0으로 클램핑하지 말고 음수 그대로 표시하고 경고를 띄워야 합니다." },
                  { title: "ROI 계산", body: "ROI = ((V - R - C - P) / P) × 100. P가 0 이하일 경우 ROI 계산을 건너뛰고 'N/A'로 표시합니다." },
                  { title: "민감도 분석", body: "시세 ±10% 시나리오에서 취득세도 조정된 시세 기준으로 재계산해야 합니다. 일반방식에는 없는 기능입니다." },
                  { title: "금액 표시", body: "모든 금액은 원(₩) 단위 정수입니다. 소수점 이하 금액은 발생하지 않습니다. 천 단위 쉼표로 표시합니다." },
                ].map((item, i) => (
                  <div key={i} className="bg-[#FAFAF8] rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <i className="ri-error-warning-line text-[#C4956A] text-sm mt-0.5 flex-shrink-0"></i>
                      <div>
                        <p className="text-xs font-semibold text-[#1C1917] mb-0.5">{item.title}</p>
                        <p className="text-xs text-[#5C5650] leading-relaxed">{item.body}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Complete Pseudocode */}
            <div className="bg-white rounded-xl border border-[#E8E5E0] p-6">
              <h3 className="text-sm font-bold text-[#1C1917] mb-3">전체 의사코드 (Python 스타일)</h3>
              <div className="bg-[#1C1917] rounded-lg p-4 text-xs font-mono text-[#B8B3AE] leading-relaxed overflow-x-auto">
                <p><span className="text-[#8C8780]"># ===== 3종 하드코딩 테이블 =====</span></p>
                <p></p>
                <p><span className="text-[#C4956A]">def</span> <span className="text-emerald-400">get_age_weight</span>(age):</p>
                <p>    <span className="text-[#C4956A]">if</span> age {"<="} 5:  <span className="text-[#C4956A]">return</span> 0.5</p>
                <p>    <span className="text-[#C4956A]">if</span> age {"<="} 10: <span className="text-[#C4956A]">return</span> 1.0</p>
                <p>    <span className="text-[#C4956A]">if</span> age {"<="} 20: <span className="text-[#C4956A]">return</span> 1.5</p>
                <p>    <span className="text-[#C4956A]">return</span> 2.0</p>
                <p></p>
                <p><span className="text-[#C4956A]">def</span> <span className="text-emerald-400">get_eviction_cost</span>(area, occupant_type):</p>
                <p>    table = {"{"}</p>
                <p>        <span className="text-amber-300">"debtor"</span>:            (2_000_000, 30_000),</p>
                <p>        <span className="text-amber-300">"tenant_no_rights"</span>:  (3_000_000, 50_000),</p>
                <p>        <span className="text-amber-300">"tenant_with_rights"</span>: (10_000_000, 120_000),</p>
                <p>        <span className="text-amber-300">"lien"</span>:              (15_000_000, 150_000),</p>
                <p>    {"}"}</p>
                <p>    <span className="text-[#C4956A]">if</span> occupant_type <span className="text-[#C4956A]">not in</span> table:</p>
                <p>        <span className="text-[#C4956A]">return</span> 0</p>
                <p>    _min, unit = table[occupant_type]</p>
                <p>    <span className="text-[#C4956A]">return</span> max(_min, area * unit)</p>
                <p></p>
                <p><span className="text-[#C4956A]">def</span> <span className="text-emerald-400">get_tax_rate</span>(home_count, price):</p>
                <p>    <span className="text-[#C4956A]">if</span> home_count {"<="} 1:</p>
                <p>        <span className="text-[#C4956A]">if</span> price {"<="} 600_000_000:  <span className="text-[#C4956A]">return</span> 0.011</p>
                <p>        <span className="text-[#C4956A]">if</span> price {"<="} 900_000_000:  <span className="text-[#C4956A]">return</span> 0.013</p>
                <p>        <span className="text-[#C4956A]">return</span> 0.035</p>
                <p>    <span className="text-[#C4956A]">if</span> home_count == 2:  <span className="text-[#C4956A]">return</span> 0.08</p>
                <p>    <span className="text-[#C4956A]">return</span> 0.12</p>
                <p></p>
                <p><span className="text-[#8C8780]"># ===== 일반방식 계산 =====</span></p>
                <p></p>
                <p><span className="text-[#C4956A]">def</span> <span className="text-emerald-400">calculate_normal</span>(data):</p>
                <p>    repair = round(data.area * 50000 * get_age_weight(data.age))</p>
                <p>    eviction = get_eviction_cost(data.area, data.occupant)</p>
                <p>    tax_rate = get_tax_rate(data.homes, data.est_price)</p>
                <p>    tax = round(data.est_price * tax_rate)</p>
                <p>    finance = round(data.loan * (data.rate/100) * (data.months/12))</p>
                <p>    mgmt = data.mgmt_fee <span className="text-[#C4956A]">or</span> round(data.area * 20000)</p>
                <p>    total_cost = repair + eviction + tax + finance + mgmt</p>
                <p>    p_max = data.est_price - data.takeover - total_cost - data.target_profit</p>
                <p>    safe = round(p_max * 0.95)</p>
                <p>    winning_adj = round(data.est_price * (data.win_rate / 100))</p>
                <p>    recommended = min(p_max, winning_adj)</p>
                <p>    <span className="text-[#C4956A]">return</span> safe, recommended, p_max, total_cost</p>
                <p></p>
                <p><span className="text-[#8C8780]"># ===== 시세역산방식 계산 =====</span></p>
                <p></p>
                <p><span className="text-[#C4956A]">def</span> <span className="text-emerald-400">calculate_reverse</span>(data):</p>
                <p>    repair = round(data.area * 50000 * get_age_weight(data.age))</p>
                <p>    eviction = get_eviction_cost(data.area, data.occupant)</p>
                <p>    tax_rate = get_tax_rate(data.homes, data.acute_price) <span className="text-[#8C8780]"># V 기준!</span></p>
                <p>    tax = round(data.acute_price * tax_rate)</p>
                <p>    finance = round(data.loan * (data.rate/100) * (data.months/12))</p>
                <p>    mgmt = data.mgmt_fee <span className="text-[#C4956A]">or</span> round(data.area * 20000)</p>
                <p>    C = repair + eviction + tax + finance + mgmt + data.brokerage + data.extra</p>
                <p>    P = data.acute_price - data.margin - C - data.takeover</p>
                <p>    safe = round(P * 0.95)</p>
                <p>    danger = P {"<"} 0</p>
                <p>    over_min = P {"<"} data.min_bid <span className="text-[#C4956A]">if not</span> danger <span className="text-[#C4956A]">else</span> False</p>
                <p>    <span className="text-[#C4956A]">return</span> P, safe, C, danger, over_min</p>
              </div>
            </div>
          </section>

          {/* Footer note */}
          <footer className="mt-16 pt-8 border-t border-[#E8E5E0] text-center">
            <p className="text-xs text-[#B8B3AE]">
              &copy; 2026 강스터디. 본 문서는 경매 입찰가 계산기의 기술 명세 목적으로 작성되었습니다.<br />
              문의: 강스터디 고객센터 | 버전: v2.0 | 문서 버전: 1.0
            </p>
          </footer>

        </div>
      </main>
      <Footer />
    </div>
  );
}